import {
  Sparkles,
  Waves,
  Sun,
  Moon,
  Wind,
  Heart,
  Brain,
  Shield,
  Zap,
  Feather,
  Compass,
  Star,
  Flower2,
  Flame,
  Droplets,
  Activity,
  Eye,
  Infinity,
  Anchor,
  Cloud,
  Sunrise,
  Gem,
} from 'lucide-react'

export interface Service {
  id: string
  title: string
  slug: string
  shortDescription: string
  duration: string
  price: number
  icon: any
  benefits: string[]
  whatToExpect: string[]
  idealFor: string
  isPremium?: boolean
}

export const services: Service[] = [
  {
    id: 'chakra-balancing',
    title: 'Chakra Balancing',
    slug: 'chakra-balancing',
    shortDescription:
      'Restore harmony across your seven energy centers to improve clarity, confidence, and peace.',
    duration: '60 Minutes',
    price: 79,
    icon: Activity,
    benefits: [
      'Increased physical vitality and energy',
      'Greater emotional stability and resilience',
      'Enhanced mental clarity and focus',
      'Deeper spiritual connection',
    ],
    whatToExpect: [
      'Initial energy assessment and consultation',
      'Guided relaxation and grounding meditation',
      'Targeted energy clearing for each chakra',
      'Post-session discussion and integration guidance',
    ],
    idealFor:
      'Feeling stuck, experiencing physical or emotional fatigue, seeking overall balance.',
  },
  {
    id: 'aura-cleansing',
    title: 'Aura Cleansing',
    slug: 'aura-cleansing',
    shortDescription:
      'Clear stagnant energy, emotional heaviness, and energetic interference from your vibrational field.',
    duration: '45 Minutes',
    price: 65,
    icon: Sparkles,
    benefits: [
      'Feeling lighter and more expansive',
      "Release of other people's energy",
      'Stronger energetic boundaries',
      'Renewed sense of personal space',
    ],
    whatToExpect: [
      'Aura scanning to identify heavy or stagnant areas',
      'Energetic sweeping and clearing techniques',
      'Sealing and protecting the auric field',
      'Grounding exercises to anchor the new energy',
    ],
    idealFor:
      'Empaths, those working in high-stress environments, feeling drained after social interactions.',
  },
  {
    id: 'astrology-reading',
    title: 'Astrology Reading',
    slug: 'astrology-reading',
    shortDescription:
      'Receive practical life guidance through detailed birth chart analysis and cosmic blueprint exploration.',
    duration: '75 Minutes',
    price: 120,
    icon: Star,
    benefits: [
      'Deep validation of your inherent nature',
      'Clarity on life purpose and direction',
      'Understanding of relationship dynamics',
      'Foresight regarding upcoming life themes',
    ],
    whatToExpect: [
      'In-depth analysis of your Sun, Moon, and Rising signs',
      'Exploration of major planetary placements and aspects',
      'Discussion of current transits affecting your chart',
      'Time for specific questions about love, career, or life path',
    ],
    idealFor:
      'Seeking life direction, navigating major transitions, wanting deeper self-understanding.',
    isPremium: true,
  },
  {
    id: 'energy-healing',
    title: 'Energy Healing Therapy',
    slug: 'energy-healing-therapy',
    shortDescription:
      'A comprehensive session to release deep-seated emotional pain and restore profound energetic wellness.',
    duration: '90 Minutes',
    price: 149,
    icon: Zap,
    benefits: [
      'Release of deep emotional blockages',
      'Reduction in physical tension and stress',
      'Profound sense of peace and relaxation',
      'Accelerated personal healing journey',
    ],
    whatToExpect: [
      'Detailed consultation regarding your healing goals',
      'Combination of Reiki, sound healing, and intuitive energy work',
      'Focus on specific areas of physical or emotional holding',
      'Extensive integration time and personalized aftercare plan',
    ],
    idealFor:
      'Processing grief or trauma, chronic stress, seeking a deep reset.',
    isPremium: true,
  },
  {
    id: 'negative-energy-removal',
    title: 'Negative Energy Removal',
    slug: 'negative-energy-removal',
    shortDescription:
      'Safely detach and clear persistent negative attachments, thought forms, and environmental heaviness.',
    duration: '60 Minutes',
    price: 85,
    icon: Shield,
    benefits: [
      'Immediate relief from unexplained heaviness',
      'Restored optimism and mental brightness',
      'Protection from future energetic drains',
      'Improved sleep and dream quality',
    ],
    whatToExpect: [
      'Diagnostic scan for energetic attachments',
      'Deep clearing using sound and smoke (virtual visualization)',
      'Cord-cutting visualization techniques',
      'Establishment of energetic shields',
    ],
    idealFor:
      'Feeling constantly drained, experiencing sudden mood drops, or feeling "cursed" or stuck.',
  },
  {
    id: 'emotional-healing',
    title: 'Emotional Healing',
    slug: 'emotional-healing',
    shortDescription:
      'Gently process and release trapped emotions that are preventing you from moving forward.',
    duration: '60 Minutes',
    price: 90,
    icon: Heart,
    benefits: [
      'Release of suppressed grief or anger',
      'Lighter emotional baseline',
      'Improved relationships and communication',
      'Greater capacity for joy',
    ],
    whatToExpect: [
      'Safe, compassionate space for emotional expression',
      'Somatic body-awareness exercises',
      'Guided release visualizations',
      'Heart-chakra soothing techniques',
    ],
    idealFor:
      'Recent breakups, lingering sadness, feeling emotionally numb or overwhelmed.',
  },
  {
    id: 'stress-relief',
    title: 'Stress Relief Sessions',
    slug: 'stress-relief-sessions',
    shortDescription:
      'Melt away accumulated daily tension and reset your nervous system to a state of calm.',
    duration: '45 Minutes',
    price: 65,
    icon: Waves,
    benefits: [
      'Lowered cortisol levels',
      'Relaxed muscle tension',
      'Slower, deeper breathing patterns',
      'Quiet mind',
    ],
    whatToExpect: [
      'Progressive muscle relaxation',
      'Vagus nerve soothing exercises',
      'Gentle energy sweeping',
      'Calming sound frequencies',
    ],
    idealFor: 'Burnout, high-pressure careers, general daily overwhelm.',
  },
  {
    id: 'anxiety-relaxation',
    title: 'Anxiety Relaxation Guidance',
    slug: 'anxiety-relaxation-guidance',
    shortDescription:
      'Specialized techniques to ground racing thoughts and soothe an overactive nervous system.',
    duration: '45 Minutes',
    price: 70,
    icon: Feather,
    benefits: [
      'Immediate reduction in panic feelings',
      'Tools to manage future anxiety spikes',
      'Restored sense of safety in the body',
      'Clearer, slower thinking',
    ],
    whatToExpect: [
      'Immediate grounding protocols',
      'Breathwork specifically for anxiety',
      'Root chakra stabilization',
      'Creation of a personal "safe space" visualization',
    ],
    idealFor: 'Chronic worriers, those experiencing panic, feeling ungrounded.',
  },
  {
    id: 'guided-meditation',
    title: 'Guided Meditation',
    slug: 'guided-meditation',
    shortDescription:
      'A personalized grounding and calming meditation experience tailored to your current energetic needs.',
    duration: '30 Minutes',
    price: 45,
    icon: Brain,
    benefits: [
      'Immediate stress reduction',
      'Lowered anxiety levels',
      'Improved sleep quality',
      'Enhanced ability to remain present',
    ],
    whatToExpect: [
      'Brief check-in to determine focus',
      'Guided breathwork to center the mind',
      'Customized visualization journey',
      'Gentle return to waking consciousness',
    ],
    idealFor: 'High anxiety, difficulty sleeping, beginners to meditation.',
  },
  {
    id: 'deep-relaxation',
    title: 'Deep Relaxation Therapy',
    slug: 'deep-relaxation-therapy',
    shortDescription:
      'Enter a state of profound rest, allowing your body and mind to heal themselves naturally.',
    duration: '60 Minutes',
    price: 80,
    icon: Moon,
    benefits: [
      'Experience of "Yoga Nidra" state (conscious sleep)',
      'Deep cellular repair',
      'Release of chronic holding patterns',
      'Complete mental reset',
    ],
    whatToExpect: [
      'Extended body scan',
      'Binaural beats and soundscapes',
      'Minimal talking, maximum resting',
      'Gentle awakening process',
    ],
    idealFor:
      'Exhaustion, chronic fatigue, those who find it hard to "switch off".',
  },
  {
    id: 'spiritual-energy-balancing',
    title: 'Spiritual Energy Balancing',
    slug: 'spiritual-energy-balancing',
    shortDescription:
      'Align your physical, mental, and spiritual bodies for a cohesive, powerful presence.',
    duration: '75 Minutes',
    price: 110,
    icon: Infinity,
    benefits: [
      'Alignment with higher purpose',
      'Stronger intuition',
      'Synchronicity in daily life',
      'Feeling "whole" and integrated',
    ],
    whatToExpect: [
      'Assessment of all energetic layers',
      'Alignment of the central channel (Sushumna)',
      'Integration of higher self wisdom',
      'Balancing masculine and feminine energies',
    ],
    idealFor:
      'Spiritual seekers, feeling disconnected from purpose, major life transitions.',
    isPremium: true,
  },
  {
    id: 'positive-energy-activation',
    title: 'Positive Energy Activation',
    slug: 'positive-energy-activation',
    shortDescription:
      'Ignite your inner spark and raise your vibrational frequency to attract abundance and joy.',
    duration: '45 Minutes',
    price: 75,
    icon: Sun,
    benefits: [
      'Surge of motivation and joy',
      'Magnetic aura that attracts opportunities',
      'Shift from scarcity to abundance mindset',
      'Increased creativity',
    ],
    whatToExpect: [
      'Solar plexus activation',
      'Joy-inducing visualizations',
      'Affirmation anchoring',
      'High-frequency energy transmission',
    ],
    idealFor:
      'Feeling uninspired, preparing for a big event or launch, wanting to manifest.',
  },
  {
    id: 'mind-calm',
    title: 'Mind Calm Sessions',
    slug: 'mind-calm-sessions',
    shortDescription:
      'Clear mental fog, stop overthinking, and find the quiet space between your thoughts.',
    duration: '45 Minutes',
    price: 65,
    icon: Cloud,
    benefits: [
      'Sharper focus',
      'End to rumination loops',
      'Better decision making',
      'Mental spaciousness',
    ],
    whatToExpect: [
      'Third-eye clearing',
      'Mindfulness techniques',
      'Mental decluttering visualization',
      'Focus-enhancing breathwork',
    ],
    idealFor: 'Overthinkers, students, professionals with decision fatigue.',
  },
  {
    id: 'sleep-relaxation',
    title: 'Sleep Relaxation Healing',
    slug: 'sleep-relaxation-healing',
    shortDescription:
      'Prepare your nervous system for deep, restorative, and uninterrupted sleep.',
    duration: '45 Minutes',
    price: 70,
    icon: Moon,
    benefits: [
      'Faster time to fall asleep',
      'Fewer night awakenings',
      'More vivid, pleasant dreams',
      'Waking up feeling actually rested',
    ],
    whatToExpect: [
      'Evening wind-down protocol',
      "Release of the day's energetic residue",
      'Delta-wave inducing techniques',
      'Sleep-specific guided imagery',
    ],
    idealFor: 'Insomnia, restless sleepers, those with anxiety at night.',
  },
  {
    id: 'breathwork-healing',
    title: 'Breathwork Healing',
    slug: 'breathwork-healing',
    shortDescription:
      'Use the power of conscious connected breathing to bypass the mind and heal the body.',
    duration: '60 Minutes',
    price: 85,
    icon: Wind,
    benefits: [
      'Massive emotional release',
      'Alkalized body state',
      'Expanded states of consciousness',
      'Somatic healing without talking',
    ],
    whatToExpect: [
      'Explanation of the breathing pattern',
      'Active breathing phase with music',
      'Rest and integration phase',
      'Processing and grounding',
    ],
    idealFor:
      'Those who struggle to meditate, wanting a powerful somatic release.',
  },
  {
    id: 'inner-peace',
    title: 'Inner Peace Alignment',
    slug: 'inner-peace-alignment',
    shortDescription:
      'Cultivate a deep, unshakable core of tranquility that remains steady regardless of external chaos.',
    duration: '60 Minutes',
    price: 90,
    icon: Anchor,
    benefits: [
      'Resilience to external drama',
      'Deep self-acceptance',
      'Slower, more intentional living',
      'Contentment in the present moment',
    ],
    whatToExpect: [
      'Heart and Root chakra connection',
      'Acceptance and surrender practices',
      'Energetic anchoring',
      'Cultivation of the "witness" state',
    ],
    idealFor:
      'Those surrounded by chaotic environments, seeking profound contentment.',
  },
  {
    id: 'personal-energy-reading',
    title: 'Personal Energy Reading',
    slug: 'personal-energy-reading',
    shortDescription:
      'An intuitive scan of your current energetic state, revealing blocks, strengths, and immediate needs.',
    duration: '45 Minutes',
    price: 85,
    icon: Eye,
    benefits: [
      'Clarity on why you feel the way you do',
      'Validation of your intuition',
      'Identification of hidden energetic drains',
      'Actionable advice for self-care',
    ],
    whatToExpect: [
      'Intuitive scan of your aura and chakras',
      'Feedback on what is seen/felt',
      'Discussion of current life themes',
      'Prescription for energetic hygiene',
    ],
    idealFor: 'Curious about their energy, feeling "off" but not sure why.',
  },
  {
    id: 'meditation-coaching',
    title: 'Meditation Coaching',
    slug: 'meditation-coaching',
    shortDescription:
      'Learn how to build a sustainable, personalized meditation practice that actually works for you.',
    duration: '45 Minutes',
    price: 75,
    icon: Compass,
    benefits: [
      'Overcoming the "I can\'t meditate" belief',
      'A custom practice that fits your lifestyle',
      'Understanding different meditation styles',
      'Accountability and support',
    ],
    whatToExpect: [
      'Assessment of past meditation attempts',
      'Troubleshooting common obstacles',
      'Trial of 2-3 different techniques',
      'Creation of a realistic daily plan',
    ],
    idealFor: 'Beginners, those who have tried and failed to build a habit.',
  },
  {
    id: 'confidence-motivation',
    title: 'Confidence & Motivation Healing',
    slug: 'confidence-motivation-healing',
    shortDescription:
      'Clear self-doubt and imposter syndrome while activating your inner fire and drive.',
    duration: '60 Minutes',
    price: 90,
    icon: Flame,
    benefits: [
      'Boldness in decision making',
      'Belief in your own worth',
      'Energy to tackle procrastinated tasks',
      'Stronger personal boundaries',
    ],
    whatToExpect: [
      'Solar Plexus and Throat chakra clearing',
      'Release of limiting beliefs',
      'Empowerment visualization',
      'Action-step planning',
    ],
    idealFor:
      'Entrepreneurs, artists, anyone facing a big leap or struggling with self-worth.',
  },
  {
    id: 'emotional-detox',
    title: 'Emotional Detox Sessions',
    slug: 'emotional-detox-sessions',
    shortDescription:
      'A deep cleanse for your emotional body, flushing out resentment, guilt, and old hurt.',
    duration: '75 Minutes',
    price: 115,
    icon: Droplets,
    benefits: [
      'Feeling washed clean internally',
      'Forgiveness of self and others',
      'Lighter physical feeling',
      'Renewed capacity to love',
    ],
    whatToExpect: [
      'Water-element visualizations',
      "Forgiveness protocols (Ho'oponopono)",
      'Sacral and Heart chakra flushing',
      'Deep replenishing energy work',
    ],
    idealFor:
      'Holding onto past hurts, feeling toxic or bitter, post-argument recovery.',
  },
  {
    id: 'spiritual-wellness',
    title: 'Spiritual Wellness Guidance',
    slug: 'spiritual-wellness-guidance',
    shortDescription:
      'Holistic mentoring to integrate spiritual practices into your modern, busy life.',
    duration: '60 Minutes',
    price: 95,
    icon: Flower2,
    benefits: [
      'A cohesive spiritual routine',
      'Bridging the gap between mundane and sacred',
      'Consistent growth and learning',
      'Supportive sounding board',
    ],
    whatToExpect: [
      'Review of current practices',
      'Lifestyle assessment',
      'Introduction of new tools (crystals, herbs, rituals)',
      'Ongoing strategy and support',
    ],
    idealFor:
      'Those wanting to live more intentionally, seeking a spiritual mentor.',
  },
  {
    id: 'focus-clarity',
    title: 'Focus & Clarity Sessions',
    slug: 'focus-clarity-sessions',
    shortDescription:
      'Cut through confusion and overwhelm to find your clear next step and the focus to take it.',
    duration: '45 Minutes',
    price: 75,
    icon: Sunrise,
    benefits: [
      'Crystal clear direction',
      'Elimination of distractions',
      'Renewed sense of purpose',
      'Actionable clarity',
    ],
    whatToExpect: [
      'Grounding to stop the spin',
      'Third-eye activation for insight',
      'Guided questioning to reveal your truth',
      'Distillation into one clear action',
    ],
    idealFor: 'At a crossroads, feeling scattered, overwhelmed by options.',
  },
  {
    id: 'premium-healing-programs',
    title: 'Premium Healing Programs',
    slug: 'premium-healing-programs',
    shortDescription:
      'Multi-week, intensive transformational journeys combining all modalities for profound life shifts.',
    duration: '4-12 Weeks',
    price: 499,
    icon: Gem,
    benefits: [
      'Complete life transformation',
      'Sustained, supported healing',
      'Mastery of personal energy',
      'Deep, lasting results',
    ],
    whatToExpect: [
      'Weekly 90-minute intensive sessions',
      'Unlimited WhatsApp support between sessions',
      'Custom rituals, meditations, and homework',
      'Comprehensive astrology and energy tracking',
    ],
    idealFor:
      'Ready for massive change, committed to doing the deep work, seeking VIP support.',
    isPremium: true,
  },
]
